import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';

interface HistoryRecord {
  id: number;
  exchange: string;
  symbol: string;
  direction: 'LONG' | 'SHORT';
  entry_price: number;
  exit_price: number | null;
  entry_amount_usdt: number;
  profit_loss_usdt: number | null;
  profit_loss_percent: number | null;
  exit_reason: string;
  opened_at: string;
  closed_at: string;
}

function fmtPrice(n: number | null) {
  if (n === null) return '—';
  if (n >= 1000) return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (n >= 1) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(6)}`;
}

function exitTagClass(reason: string) {
  const r = reason?.toUpperCase() || '';
  if (r.includes('TAKE_PROFIT') || r.includes('TP')) return 'pos-exit-tag pos-exit-tp';
  if (r.includes('STOP_LOSS') || r.includes('SL')) return 'pos-exit-tag pos-exit-sl';
  if (r.includes('TRAIL')) return 'pos-exit-tag pos-exit-trail';
  return 'pos-exit-tag pos-exit-manual';
}

function exitTagLabel(reason: string) {
  const r = reason?.toUpperCase() || '';
  if (r.includes('TAKE_PROFIT') || r.includes('TP')) return 'Take Profit';
  if (r.includes('STOP_LOSS') || r.includes('SL')) return 'Stop Loss';
  if (r.includes('TRAIL')) return 'Trail Stop';
  return 'Manual';
}

function platformClass(exchange: string) {
  if (exchange === 'HYPERLIQUID') return 'hl';
  if (exchange === 'BINANCE') return 'bn';
  return 'bg';
}
function platformShort(exchange: string) {
  if (exchange === 'HYPERLIQUID') return 'HL';
  if (exchange === 'BINANCE') return 'BN';
  return 'BG';
}

function fmtDate(s: string) {
  const d = new Date(s);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', ' +
    d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

interface HistoryStats {
  total_trades: number;
  total_pnl: number | null;
  win_rate: number | null;
  wins: number;
  losses: number;
  hl_pnl: number | null;
  bn_pnl: number | null;
  bg_pnl: number | null;
}

interface Props {
  onBack: () => void;
}

const PositionHistory: React.FC<Props> = ({ onBack }) => {
  const [records, setRecords] = useState<HistoryRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<HistoryStats | null>(null);
  const [page, setPage] = useState(1);
  const [exchange, setExchange] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = { page, limit };
      if (exchange) params.exchange = exchange;
      const res = await api.get('/positions/history', { params });
      setRecords(res.data.records || []);
      setTotal(res.data.total || 0);
      if (res.data.stats) setStats(res.data.stats);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [page, exchange]);

  useEffect(() => { load(); }, [load]);

  const exchanges = ['', 'HYPERLIQUID', 'BINANCE', 'BITGET'];
  const exLabels: Record<string, string> = { '': 'All', HYPERLIQUID: 'Hyperliquid', BINANCE: 'Binance', BITGET: 'Bitget' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3 }}>
        <button className="pos-btn pos-btn-outline" style={{ padding: '5px 10px', fontSize: 12 }} onClick={onBack}>
          ← Back
        </button>
        <div className="pos-page-title" style={{ marginBottom: 0 }}>Closed Positions</div>
      </div>
      <div className="pos-page-sub">Historical copy trade records</div>

      <div className="pos-hist-filters">
        {exchanges.map(ex => (
          <button
            key={ex}
            className={`pos-btn ${exchange === ex ? 'pos-btn-primary' : 'pos-btn-outline'}`}
            style={{ padding: '6px 12px', fontSize: 12 }}
            onClick={() => { setExchange(ex); setPage(1); }}
          >
            {exLabels[ex]}
          </button>
        ))}
      </div>

      {/* Stats Row */}
      {stats && stats.total_trades > 0 && (
        <div className="ph-stats-row">
          <div className="ph-stat-card ph-stat-total">
            <div className="ph-stat-label">Total Trades</div>
            <div className="ph-stat-val">{stats.total_trades}</div>
            <div className="ph-stat-sub">{stats.wins}W · {stats.losses}L</div>
          </div>
          <div className="ph-stat-card ph-stat-winrate">
            <div className="ph-stat-label">Win Rate</div>
            <div className={`ph-stat-val ${(stats.win_rate ?? 0) >= 50 ? 'green' : 'red'}`}>
              {stats.win_rate !== null ? `${stats.win_rate.toFixed(1)}%` : '—'}
            </div>
            <div className="ph-stat-sub">closed positions</div>
          </div>
          <div className={`ph-stat-card ph-stat-pnl ${(stats.total_pnl ?? 0) >= 0 ? 'ph-pnl-pos' : 'ph-pnl-neg'}`}>
            <div className="ph-stat-label">Total Realized</div>
            <div className={`ph-stat-val ${(stats.total_pnl ?? 0) >= 0 ? 'green' : 'red'}`}>
              {stats.total_pnl !== null
                ? `${stats.total_pnl >= 0 ? '+' : ''}$${stats.total_pnl.toFixed(2)}`
                : '—'}
            </div>
            <div className="ph-stat-sub">USDT</div>
          </div>
          <div className="ph-stat-card ph-stat-platforms">
            <div className="ph-stat-label">By Platform</div>
            <div className="ph-platform-list">
              {[
                { key: 'HL', val: stats.hl_pnl, cls: 'hl' },
                { key: 'BN', val: stats.bn_pnl, cls: 'bn' },
                { key: 'BG', val: stats.bg_pnl, cls: 'bg' },
              ].filter(p => p.val !== null && p.val !== 0).map(p => (
                <div key={p.key} className="ph-platform-row">
                  <span className={`pos-platform ${p.cls}`} style={{ fontSize: 10, padding: '2px 7px' }}>
                    <span className="pos-platform-dot" />{p.key}
                  </span>
                  <span className={`ph-platform-pnl ${(p.val ?? 0) >= 0 ? 'green' : 'red'}`}>
                    {(p.val ?? 0) >= 0 ? '+' : ''}${(p.val ?? 0).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {error && <div className="pos-error">{error}</div>}

      {loading ? (
        <div className="pos-loading"><span className="pos-spinner" />Loading...</div>
      ) : records.length === 0 ? (
        <div className="pos-empty">
          <div className="pos-empty-icon">📭</div>
          <div className="pos-empty-text">No closed positions</div>
        </div>
      ) : (
        <div className="pos-hist-wrap">
          <table className="pos-hist-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Platform</th>
                <th>Dir</th>
                <th>Entry</th>
                <th>Exit</th>
                <th>PnL (USDT)</th>
                <th>PnL %</th>
                <th>Exit Reason</th>
                <th>Closed At</th>
              </tr>
            </thead>
            <tbody>
              {records.map(r => {
                const pnl = r.profit_loss_usdt;
                const pnlPct = r.profit_loss_percent;
                const pnlClass = pnl === null ? '' : pnl >= 0 ? 'green' : 'red';
                const pnlSign = pnl !== null && pnl >= 0 ? '+' : '';
                const pctSign = pnlPct !== null && pnlPct >= 0 ? '+' : '';
                return (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 700, color: '#1a1a1a' }}>{r.symbol}</td>
                    <td>
                      <span className={`pos-platform ${platformClass(r.exchange)}`} style={{ fontSize: 10 }}>
                        <span className="pos-platform-dot" />{platformShort(r.exchange)}
                      </span>
                    </td>
                    <td><span className={`pos-dir ${r.direction.toLowerCase()}`}>{r.direction}</span></td>
                    <td>{fmtPrice(r.entry_price)}</td>
                    <td>{fmtPrice(r.exit_price)}</td>
                    <td className={`pos-hist-pnl ${pnlClass}`}>
                      {pnl === null ? '—' : `${pnlSign}$${Math.abs(pnl).toFixed(2)}`}
                    </td>
                    <td className={`pos-hist-pnl ${pnlClass}`}>
                      {pnlPct === null ? '—' : `${pctSign}${pnlPct.toFixed(1)}%`}
                    </td>
                    <td><span className={exitTagClass(r.exit_reason)}>{exitTagLabel(r.exit_reason)}</span></td>
                    <td style={{ color: '#9ca3af', fontSize: 12 }}>{fmtDate(r.closed_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > limit && (
        <div className="pos-paging">
          <span className="pos-paging-info">{(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}</span>
          <button className="pos-btn pos-btn-outline" style={{ padding: '5px 12px', fontSize: 12 }}
            disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <button className="pos-btn pos-btn-outline" style={{ padding: '5px 12px', fontSize: 12 }}
            disabled={page * limit >= total} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
};

export default PositionHistory;
